import React, { useState, useEffect, useMemo } from 'react';
import Fuse from 'fuse.js';
import { CharacterData } from '../../types/character';
import { LootItem, ConsumableItem } from '../../types/equipment';
import { itemLoader } from '../../utils/itemLoader';
import { filterBySubtype, filterOfficial, sortItems } from '../../utils/itemFilters';
import { distributeItemsIntoColumns, getColumnCount } from '../../utils/columnDistribution';
import { createFuseInstance, performFuzzySearch } from '../../utils/fuseSearchConfig';
import { ItemLibraryFilters, ItemFilters } from './ItemLibraryFilters';
import { ItemCard } from '../Equipment/ItemDisplay/ItemCard';
import { GenericItem } from './ItemBrowser';
import styles from './ItemBrowser.module.css';

interface LootConsumableBrowserProps {
  character: CharacterData;
  onAddToCharacter: (itemId: string) => Promise<void>;
  itemType: 'loot' | 'consumable';
}

/**
 * LootConsumableBrowser - Browse and add loot or consumable items to character
 * Provides fuzzy search, filtering, and sorting capabilities
 * Unified component for both loot and consumable item types
 */
export const LootConsumableBrowser: React.FC<LootConsumableBrowserProps> = ({
  character,
  onAddToCharacter,
  itemType
}) => {
  const [allItems, setAllItems] = useState<(LootItem | ConsumableItem)[]>([]);
  const [filteredItems, setFilteredItems] = useState<(LootItem | ConsumableItem)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [columnCount, setColumnCount] = useState(getColumnCount(window.innerWidth));

  const [filters, setFilters] = useState<ItemFilters>({
    subtype: null,
    official: true,
    search: '',
    sort: 'name'
  });

  // Create Fuse instance for fuzzy search
  const fuseInstance = useMemo<Fuse<GenericItem> | null>(() => {
    if (allItems.length === 0) return null;
    // Cast items to GenericItem for Fuse compatibility
    const genericItems = allItems as unknown as GenericItem[];
    return createFuseInstance(genericItems);
  }, [allItems]);

  // Load items on mount or when itemType changes
  useEffect(() => {
    const loadItems = async () => {
      try {
        setLoading(true);
        await itemLoader.initialize();

        const items = itemType === 'loot'
          ? itemLoader.getAllLoot()
          : itemLoader.getAllConsumables();

        setAllItems(items);
        setError(null);
      } catch (err) {
        console.error(`Failed to load ${itemType} items:`, err);
        setError(`Failed to load ${itemType} items`);
      } finally {
        setLoading(false);
      }
    };

    loadItems();
  }, [itemType]);

  // Apply filters whenever they change
  useEffect(() => {
    if (allItems.length === 0) {
      setFilteredItems([]);
      return;
    }

    let results = [...allItems];

    // 1. Apply Fuse fuzzy search (if search query exists)
    if (filters.search.trim() && fuseInstance) {
      const fuzzyResults = performFuzzySearch(fuseInstance, filters.search);
      // Extract items from search results
      results = fuzzyResults.map(result => result.item as unknown as (LootItem | ConsumableItem));
    }

    // 2. Apply subtype filter
    if (filters.subtype) {
      results = filterBySubtype(results, filters.subtype);
    }

    // 3. Apply official filter
    results = filterOfficial(results, filters.official);

    // 4. Sort results
    results = sortItems(results, filters.sort);

    setFilteredItems(results);
  }, [filters, allItems, fuseInstance]);

  // Update column count on window resize
  useEffect(() => {
    const handleResize = () => {
      setColumnCount(getColumnCount(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleFiltersChange = (newFilters: ItemFilters) => {
    setFilters(newFilters);
  };

  const handleExpandToggle = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className={styles.browserContainer}>
        <div className={styles.loadingState}>
          <p>Loading {itemType} items...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.browserContainer}>
        <div className={styles.errorState}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.browserContainer}>
      <ItemLibraryFilters
        itemType={itemType}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      <div className={styles.resultsHeader}>
        <p className={styles.resultsCount}>
          {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'} found
        </p>
      </div>

      {filteredItems.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No items match your filters</p>
          <p className={styles.emptyHint}>Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className={styles.itemGrid}>
          {distributeItemsIntoColumns(filteredItems, columnCount).map((columnItems, colIndex) => (
            <div key={colIndex} className={styles.itemColumn}>
              {columnItems.map(item => (
                <ItemCard
                  key={item.id}
                  item={item}
                  isExpanded={expandedItems.has(item.id)}
                  onExpandToggle={() => handleExpandToggle(item.id)}
                  onAddToCharacter={onAddToCharacter}
                  character={character}
                  variant="library"
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
